import { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Globe, RefreshCw, ClipboardPaste, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const RECIPE_SITES = [
  { name: "Matprat", url: "https://www.matprat.no/oppskrifter/", color: "bg-red-50 text-red-800 border-red-100" },
  { name: "Godt.no", url: "https://www.godt.no/oppskrifter", color: "bg-orange-50 text-orange-800 border-orange-100" },
  { name: "Trines matblogg", url: "https://trinesmatblogg.no/", color: "bg-amber-50 text-amber-800 border-amber-100" },
  { name: "Gladkokken", url: "https://www.gladkokken.no/oppskrifter", color: "bg-emerald-50 text-emerald-800 border-emerald-100" },
  { name: "Rema 1000", url: "https://www.rema.no/oppskrifter", color: "bg-blue-50 text-blue-800 border-blue-100" },
  { name: "Kokkelure", url: "https://kokkelure.no/", color: "bg-purple-50 text-purple-800 border-purple-100" },
  { name: "Meny.no", url: "https://meny.no/oppskrifter/", color: "bg-rose-50 text-rose-800 border-rose-100" },
  { name: "Tine", url: "https://www.tine.no/oppskrifter", color: "bg-sky-50 text-sky-800 border-sky-100" },
  { name: "Bakeprosjektet", url: "https://bakeprosjektet.no/", color: "bg-pink-50 text-pink-800 border-pink-100" },
  { name: "Ida Gran-Jansen", url: "https://idagranjansen.com/oppskrifter", color: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-100" },
  { name: "Oppskrift Klikk", url: "https://oppskrift.klikk.no/", color: "bg-teal-50 text-teal-800 border-teal-100" },
];

/**
 * Single-recipe URL importer — mobile-first redesign:
 *   • sticky footer with primary "Importer"-knapp (alltid synleg)
 *   • search row stables (source select on its own line, input + button below)
 *   • chips i horisontal scrollar i staden for 2-kol grid (sparar høgde)
 *   • éin "Lim inn"-knapp ved sida av URL-feltet (i staden for ein per chip)
 */
export default function URLImportDialog({ open, onOpenChange, servings, onImported }) {
  const [urlInput, setUrlInput] = useState("");
  const [mealType, setMealType] = useState("dinner");
  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState("matprat");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const importUrl = async (url, title) => {
    if (!url?.trim()) return;
    setLoading(true);
    if (title) toast.info("Importerer: " + title);
    try {
      const res = await api.importUrlRecipe({
        url: url.trim(), meal_type: mealType, servings, is_baking: false,
      });
      toast.success(title ? "Importert fra nett!" : "Oppskrift importert fra nett!");
      onOpenChange(false);
      setUrlInput("");
      setSearchQuery("");
      setSearchResults([]);
      onImported?.(res.data?.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved URL-import");
    } finally {
      setLoading(false);
    }
  };

  const runSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const res = await api.searchRecipeFromSource(searchKey, searchQuery.trim());
      setSearchResults(res.data?.results || []);
      if (!res.data?.results?.length) toast.info("Ingen treff — prøv et annet søk");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Søk feilet");
    } finally {
      setSearchLoading(false);
    }
  };

  const pasteFromClipboard = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      if (txt && /^https?:\/\//i.test(txt.trim())) {
        setUrlInput(txt.trim());
        toast.success("URL limt inn – klikk Importer");
      } else {
        toast.info("Ingen gyldig URL i utklippstavla");
      }
    } catch {
      toast.error("Tillat utklippstavla i nettleseren");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] flex flex-col p-0" data-testid="url-import-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Globe className="h-5 w-5 text-navy-800" />Importer fra nett
          </DialogTitle>
          <DialogDescription className="text-xs">Lim inn ein URL eller søk direkte. AI hentar og strukturerer oppskrifta.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          <div className="space-y-4">
            {/* URL input + Lim inn-knapp */}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Lenke til oppskrift</label>
              <div className="flex gap-2">
                <input
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://www.eksempel.no/oppskrift…"
                  className="flex-1 min-w-0 rounded-md border border-stone-200 bg-white px-3 py-2.5 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
                  data-testid="url-import-input"
                  inputMode="url"
                  autoComplete="off"
                />
                <button
                  onClick={pasteFromClipboard}
                  className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2.5 py-2.5 text-xs font-semibold text-stone-600 hover:bg-stone-50 flex-shrink-0"
                  data-testid="url-paste-clipboard-btn"
                  title="Lim inn frå utklippstavla"
                >
                  <ClipboardPaste className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Måltidstype */}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Måltidstype</label>
              <Select value={mealType} onValueChange={setMealType}>
                <SelectTrigger data-testid="url-meal-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Frokost</SelectItem>
                  <SelectItem value="lunch">Lunsj</SelectItem>
                  <SelectItem value="dinner">Middag</SelectItem>
                  <SelectItem value="baking">Baking</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Søk direkte */}
            <div className="border-t border-stone-100 pt-4">
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">
                Søk direkte og importer
              </label>
              <Select value={searchKey} onValueChange={setSearchKey}>
                <SelectTrigger className="mb-2" data-testid="source-search-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="matprat">Matprat.no</SelectItem>
                  <SelectItem value="godt">Godt.no</SelectItem>
                  <SelectItem value="trines">Trines matblogg</SelectItem>
                  <SelectItem value="gladkokken">Gladkokken.no</SelectItem>
                  <SelectItem value="rema">Rema 1000</SelectItem>
                  <SelectItem value="kokkelure">Kokkelure.no</SelectItem>
                  <SelectItem value="meny">Meny.no</SelectItem>
                  <SelectItem value="tine">Tine.no</SelectItem>
                  <SelectItem value="bakeprosjektet">Bakeprosjektet</SelectItem>
                  <SelectItem value="idagranjansen">Ida Gran-Jansen</SelectItem>
                  <SelectItem value="klikk">Oppskrift Klikk</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !searchLoading && runSearch()}
                  placeholder="f.eks. fiskegrateng, kjøttkaker…"
                  className="flex-1 min-w-0 rounded-md border border-stone-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
                  data-testid="source-search-input"
                />
                <button
                  onClick={runSearch}
                  disabled={searchLoading || !searchQuery.trim()}
                  className="rounded-md bg-blue-600 text-white px-3 py-2.5 text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1 flex-shrink-0"
                  data-testid="source-search-btn"
                >
                  {searchLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
                  Søk
                </button>
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-1.5 mt-3" data-testid="source-search-results">
                  {searchResults.map((r) => (
                    <div key={r.url} className="flex items-start gap-2 bg-white border border-stone-100 rounded-md p-2">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 text-xs text-stone-700 hover:text-blue-700 hover:underline break-words"
                        title={r.url}
                      >
                        {r.title}
                      </a>
                      <button
                        onClick={() => importUrl(r.url, r.title)}
                        disabled={loading}
                        className="rounded bg-blue-600 text-white px-2 py-1 text-[10px] font-semibold hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
                        data-testid={`auto-import-${r.url.length}`}
                      >
                        Importer →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Snarvegar til populære kjelder — horisontal scroll på mobil */}
            <div className="border-t border-stone-100 pt-4">
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2 block">
                Snarvegar — opne sida, kopier URL, lim inn over
              </label>
              <div className="-mx-5 px-5 overflow-x-auto scrollbar-thin">
                <div className="flex gap-2 pb-1 min-w-min">
                  {RECIPE_SITES.map((site) => (
                    <a
                      key={site.name}
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap flex-shrink-0 ${site.color}`}
                      data-testid={`recipe-site-${site.name.toLowerCase().replace(/\s|\./g, "-")}`}
                    >
                      <Globe className="h-3 w-3" /> {site.name} <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {loading && (
              <div className="space-y-3 py-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-2/3" />
                <p className="text-xs text-stone-400 text-center mt-2">Henter oppskrift frå nettside…</p>
              </div>
            )}
          </div>
        </div>

        {/* Sticky footer: alltid synleg primær knapp */}
        <div className="border-t border-stone-100 px-5 py-3 bg-white">
          <button
            onClick={() => importUrl(urlInput)}
            disabled={loading || !urlInput.trim()}
            className="w-full inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-3 text-sm font-semibold text-white hover:bg-navy-900 transition-colors gap-2 disabled:opacity-50"
            data-testid="import-url-submit-btn"
          >
            <Globe className="h-4 w-4" />
            {loading ? "Importerer…" : "Importer oppskrift"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
